<script setup>
import { defineProps, ref } from 'vue'
import { provideDocExpansion } from '@/composables/useDocExpansion'
import DocSection from '@/components/Docs/Section.vue'
import JsonBlock from '@/components/Docs/JsonBlock.vue'
import EndpointBlock from '@/components/Docs/EndpointBlock.vue'
import BasicAttribute from '@/components/Docs/Attributes/BasicAttribute.vue'

defineProps({ anchor: String })

const { triggerExpandAll } = provideDocExpansion()
const allExpanded = ref(false)
const toggleAllExpansion = () => {
  allExpanded.value = !allExpanded.value
  triggerExpandAll(allExpanded.value)
}
</script>

<template>
  <doc-section title="Sources" :anchor="anchor">
    <template v-slot:left>
      <div class="mb-5">
        <p>
          Lists the data sources and source groups your organization can access,
          along with ingest statistics. Use it to discover the valid
          <code>sources</code> and <code>log_types</code> values to pass to the
          <a href="#event-search">event search</a> and
          <a href="#event-aggregate">aggregate</a> endpoints. Results are scoped to
          your grants and take no parameters.
        </p>
      </div>

      <div>
        <div class="d-flex justify-content-between align-items-center mb-4">
          <h4 class="mb-0">Response attributes</h4>
          <button @click="toggleAllExpansion" class="btn-expand-collapse">
            <font-awesome-icon :icon="['fas', allExpanded ? 'compress-alt' : 'expand-alt']" />
            {{ allExpanded ? 'Collapse All' : 'Expand All' }}
          </button>
        </div>

        <BasicAttribute name="status" type="string" description="The status of the request's response" />

        <BasicAttribute name="data" type="object" description="Sources and groups" :depth="1">
          <BasicAttribute name="sources" type="array" description="Individual data sources" :depth="2">
            <BasicAttribute name="id" type="integer" description="Source identifier" />
            <BasicAttribute name="name" type="string" description="Source slug used in queries" />
            <BasicAttribute name="display_name" type="string" description="Human-readable source name" />
            <BasicAttribute name="description" type="string" description="Optional source description" />
            <BasicAttribute name="icon" type="string" description="Optional icon identifier" />
            <BasicAttribute name="status" type="string" description="Current source status" />
            <BasicAttribute name="max_size_bytes" type="integer" description="Optional storage cap in bytes" />
            <BasicAttribute name="stats" type="object" description="Ingest statistics">
              <BasicAttribute name="ingest_jobs" type="integer" description="Number of ingest jobs" />
              <BasicAttribute name="records" type="integer" description="Total records ingested" />
              <BasicAttribute name="size_bytes" type="integer" description="Total size on disk in bytes" />
            </BasicAttribute>
          </BasicAttribute>

          <BasicAttribute name="groups" type="array" description="Source groups (collections of sources)" :depth="2">
            <BasicAttribute name="name" type="string" description="Group slug used in queries" />
            <BasicAttribute name="display_name" type="string" description="Human-readable group name" />
            <BasicAttribute name="hide_sources" type="boolean" description="Whether member sources are hidden behind the group" />
            <BasicAttribute name="owned" type="boolean" description="Whether your organization owns the group" />
            <BasicAttribute name="source_count" type="integer" description="Number of member sources" />
            <BasicAttribute name="sources" type="array" description="Member sources (same shape as top-level sources)" />
            <BasicAttribute name="stats" type="object" description="Aggregated ingest statistics for the group" />
          </BasicAttribute>
        </BasicAttribute>
      </div>
    </template>

    <template v-slot:right>
      <endpoint-block
        title="/sources"
        method="GET"
        name="Sources"
        query="/sources"
      />

      <json-block title="Response">
{
  "status": "success",
  "data": {
    "sources": [
      {
        "id": 12,
        "name": "honeypot-1",
        "display_name": "Honeypot 1",
        "status": "active",
        "stats": {
          "ingest_jobs": 4,
          "records": 152340,
          "size_bytes": 84213760
        }
      }
    ],
    "groups": [
      {
        "name": "network-sensors",
        "display_name": "Network Sensors",
        "hide_sources": false,
        "owned": true,
        "source_count": 3,
        "sources": [],
        "stats": {
          "ingest_jobs": 11,
          "records": 984210,
          "size_bytes": 512094208
        }
      }
    ]
  }
}
      </json-block>
    </template>
  </doc-section>
</template>

<style scoped>
.btn-expand-collapse {
  background-color: transparent;
  border: 1px solid var(--border-subtle);
  color: var(--text-faint);
  padding: 0.5rem 1rem;
  border-radius: var(--radius-md);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-expand-collapse:hover {
  background-color: var(--surface-overlay-2);
  color: var(--text-primary);
}
</style>
